import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, User, CheckCircle, ChevronRight, ChevronLeft, Activity } from 'lucide-react';
import { format, addDays, startOfToday } from 'date-fns';
import { Button } from '../components/ui/Button';
import { useScreenInit } from '../useScreenInit.js';
const SERVICES = ['General Medical Consultations', 'Minor Surgical Procedures', 'Safe Medical Circumcision', 'Paternity Testing (DNA)', 'Immunizations', 'Pre-Employment Medical Examinations', 'Family Planning', 'Counseling Services', 'Chronic Disease Management', 'Physiotherapy', 'Optician Services', 'Antenatal Care', 'Postnatal Care', 'Laboratory Services', 'ECG Services', 'Ultrasound Services', 'Dentistry'];
const TIME_SLOTS = ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'];
export function Booking() {
  const screenInit = useScreenInit();
  const [step, setStep] = useState(screenInit.step ?? 1);
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const today = startOfToday();
  const next14Days = Array.from({
    length: 14
  }).map((_, i) => addDays(today, i + 1));
  const handleNext = () => setStep(s => Math.min(s + 1, 5));
  const handleBack = () => setStep(s => Math.max(s - 1, 1));
  const updateForm = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const isStepValid = () => {
    switch (step) {
      case 1:
        return !!formData.service;
      case 2:
        return !!formData.date;
      case 3:
        return !!formData.time;
      case 4:
        return !!formData.name && !!formData.phone;
      default:
        return true;
    }
  };
  const STEPS = [{
    num: 1,
    title: 'Service',
    icon: <Activity className="w-5 h-5" data-id="element-534" data-mp="37zm8d14tux9k" />
  }, {
    num: 2,
    title: 'Date',
    icon: <CalendarIcon className="w-5 h-5" data-id="element-535" data-mp="3czqogq1tppz9" />
  }, {
    num: 3,
    title: 'Time',
    icon: <Clock className="w-5 h-5" data-id="element-536" data-mp="2z8c4afw6jiqm" />
  }, {
    num: 4,
    title: 'Details',
    icon: <User className="w-5 h-5" data-id="element-537" data-mp="3395trdp9t5lm" />
  }];
  return <div className="min-h-[calc(100vh-88px)] bg-brand-light py-12 px-4 sm:px-6 lg:px-8" data-id="element-538" data-mp="337kx9ijebcl4">
      <div className="max-w-3xl mx-auto" data-id="element-539" data-mp="2p81pwryes59v">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-540" data-mp="2u990szvulkkr">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-4" data-id="element-541" data-mp="2ua0w09lj9gai">
            Book an Appointment
          </h1>
          <p className="text-slate-600" data-id="element-542" data-mp="2ka9x7vpxwf4m">
            Schedule your visit to Maisha Medical Center in a few simple steps.
          </p>
        </div>

        {/* Progress Bar */}
        {step < 5 && <div className="mb-10" data-id="element-543" data-mp="iiwyjoekf3h0">
            <div className="flex items-center justify-between relative" data-id="element-544" data-mp="ihvl5da2u4w4">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full -z-10" data-id="element-545" data-mp="230phk9aa1v5i" />
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand-red rounded-full -z-10 transition-all duration-500" style={{
            width: `${(step - 1) / 3 * 100}%`
          }} data-id="element-546" data-mp="1z0nh23ga37rt" />

              {STEPS.map(s => {
            const isActive = step === s.num;
            const isPast = step > s.num;
            return <div key={s.num} className="flex flex-col items-center gap-2 bg-brand-light px-2" data-id="element-547" data-mp="2hpu3o1k70jiw">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isActive ? 'border-brand-red bg-brand-red text-white shadow-md' : isPast ? 'border-brand-red bg-white text-brand-red' : 'border-slate-300 bg-white text-slate-400'}`} data-id="element-548" data-mp="2qqjvku6c1prc">
                      {isPast ? <CheckCircle className="w-5 h-5" data-id="element-549" data-mp="yr60fdery92y" /> : s.icon}
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${isActive || isPast ? 'text-brand-navy' : 'text-slate-400'}`} data-id="element-550" data-mp="12r7pmn3bpa1c">
                      {s.title}
                    </span>
                  </div>;
          })}
            </div>
          </div>}

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-soft-lg border border-slate-100 overflow-hidden" data-id="element-551" data-mp="k1rl7ooioxfn">
          <div className="p-6 md:p-10" data-id="element-552" data-mp="p0l2ls6xd8l3">
            <AnimatePresence mode="wait" data-id="element-553" data-mp="ozjgmsodf1ol">
              {/* STEP 1: SERVICE */}
              {step === 1 && <motion.div key="step1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-554" data-mp="3sicp4r2al0bw">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-555" data-mp="3fypimatq3c3l">
                    Select a Service
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-556" data-mp="3b30edu6rd70y">
                    {SERVICES.map(service => <button key={service} onClick={() => updateForm('service', service)} className={`p-4 rounded-xl border-2 text-left transition-all ${formData.service === service ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-557" data-mp="328lypqibfuhb">
                        <span className="font-medium" data-id="element-558" data-mp="2j8yf5m3geeln">{service}</span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: DATE */}
              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-559" data-mp="2dlcycn4giet8">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-560" data-mp="2in28m0juuog9">
                    Select a Date
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-id="element-561" data-mp="2njss6xuvc26f">
                    {next14Days.map(date => {
                  const dateStr = date.toISOString();
                  const isSunday = date.getDay() === 0;
                  return <button key={dateStr} disabled={isSunday} onClick={() => updateForm('date', dateStr)} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${isSunday ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-100' : formData.date === dateStr ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-562" data-mp="3pf6x8lbialqy">
                          <span className="text-sm font-medium uppercase tracking-wider" data-id="element-563" data-mp="3ggl5bzk59udi">
                            {format(date, 'EEE')}
                          </span>
                          <span className="text-2xl font-heading font-bold" data-id="element-564" data-mp="3bjbi8qk89usu">
                            {format(date, 'd')}
                          </span>
                          <span className="text-xs" data-id="element-565" data-mp="qpybfitq9unj">{format(date, 'MMM')}</span>
                        </button>;
                })}
                  </div>
                </motion.div>}

              {/* STEP 3: TIME */}
              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-566" data-mp="2u6lcpmo444wy">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-567" data-mp="3cwb92e7iwyqz">
                    Select a Time
                  </h2>
                  <p className="text-slate-600 mb-4" data-id="element-568" data-mp="1ikwki0q915ct">
                    Showing available slots for{' '}
                    <strong className="text-brand-navy" data-id="element-569" data-mp="1nk91w5ycpk80">
                      {format(new Date(formData.date), 'EEEE, MMMM d, yyyy')}
                    </strong>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-570" data-mp="1b8ou5ear67si">
                    {TIME_SLOTS.map(time => <button key={time} onClick={() => updateForm('time', time)} className={`p-4 rounded-xl border-2 text-center transition-all ${formData.time === time ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-571" data-mp="16andf00sukk7">
                        <span className="font-medium" data-id="element-572" data-mp="1oz20xdbwrgg9">{time}</span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: DETAILS */}
              {step === 4 && <motion.div key="step4" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-573" data-mp="9juulumheeeq">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-574" data-mp="2gvabo67hi1do">
                    Patient Details
                  </h2>

                  <div className="bg-brand-lighter p-4 rounded-xl mb-6 flex flex-col sm:flex-row gap-4 justify-between border border-slate-100" data-id="element-575" data-mp="2qlvgwchny45k">
                    <div data-id="element-576" data-mp="34kd2a3k3j4kb">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-577" data-mp="34kv4dgtfwd05">
                        Service
                      </div>
                      <div className="font-medium text-brand-navy" data-id="element-578" data-mp="1a6ty4avk8nq1">
                        {formData.service}
                      </div>
                    </div>
                    <div data-id="element-579" data-mp="15awpkqqjglnt">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-580" data-mp="3gvdufl5io4oy">
                        Date & Time
                      </div>
                      <div className="font-medium text-brand-navy" data-id="element-581" data-mp="3lx33wwd3caax">
                        {format(new Date(formData.date), 'MMM d, yyyy')} at{' '}
                        {formData.time}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-582" data-mp="2y4mmmo16gmco">
                    <div data-id="element-583" data-mp="2y7qvhypl869o">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-584" data-mp="3350mhismkpzm">
                        Full Name *
                      </label>
                      <input type="text" value={formData.name} onChange={e => updateForm('name', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="John Doe" data-id="element-585" data-mp="2p6stflypm8tq" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-586" data-mp="1vbqyrs2zhn71">
                      <div data-id="element-587" data-mp="1raou2vqasixo">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-588" data-mp="1rbzy8wnnnspb">
                          Phone Number *
                        </label>
                        <input type="tel" value={formData.phone} onChange={e => updateForm('phone', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="+267 XXX XXXX" data-id="element-589" data-mp="2j2w9xtxzbrb0" />
                      </div>
                      <div data-id="element-590" data-mp="2xzx4043n7ycn">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-591" data-mp="2xym0m5e40sm2">
                          Email Address
                        </label>
                        <input type="email" value={formData.email} onChange={e => updateForm('email', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="john@example.com" data-id="element-592" data-mp="3a9v4vcntwu0u" />
                      </div>
                    </div>
                    <div data-id="element-593" data-mp="1tjj5bouxtzq">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-594" data-mp="1ve4us7ydq4v">
                        Additional Notes (Optional)
                      </label>
                      <textarea value={formData.notes} onChange={e => updateForm('notes', e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all resize-none" placeholder="Any specific concerns or symptoms?" data-id="element-595" data-mp="av9lvdo6wfct" />
                    </div>
                  </div>
                </motion.div>}

              {/* STEP 5: CONFIRMATION */}
              {step === 5 && <motion.div key="step5" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-10" data-id="element-596" data-mp="3fdxcdez2zizr">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-597" data-mp="14l2u3fb3bnip">
                    <CheckCircle className="w-10 h-10 text-green-600" data-id="element-598" data-mp="zlg7if9dgux8" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-599" data-mp="2hj32ipjghsyq">
                    Request Received!
                  </h2>
                  <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-600" data-mp="2mgut9g8d791c">
                    Thank you, {formData.name}. We have received your
                    appointment request for{' '}
                    <strong className="text-brand-navy" data-id="element-601" data-mp="2dkn8i1oyjkbt">
                      {formData.service}
                    </strong>{' '}
                    on{' '}
                    <strong className="text-brand-navy" data-id="element-602" data-mp="358nlh8fm0q80">
                      {format(new Date(formData.date), 'MMM d, yyyy')} at{' '}
                      {formData.time}
                    </strong>
                    . Our reception team will contact you shortly to confirm.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-603" data-mp="3k5y5j66bj4fc">
                    <Button href="/" variant="outline" data-id="element-604" data-mp="3oxy2cpr3dh7p">
                      Return Home
                    </Button>
                    <Button href={`https://wa.me/26773241613?text=${encodeURIComponent(`Hello, I just submitted an appointment request online.\nName: ${formData.name}\nService: ${formData.service}\nDate: ${format(new Date(formData.date), 'MMM d, yyyy')}\nTime: ${formData.time}`)}`} variant="primary" className="bg-[#25D366] hover:bg-[#20bd5a] focus:ring-[#25D366]" data-id="element-605" data-mp="2mzd8g1kipmj">
                      Follow up on WhatsApp
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer Actions */}
          {step < 5 && <div className="bg-slate-50 p-6 border-t border-slate-100 flex items-center justify-between" data-id="element-606" data-mp="3178zse0wbchh">
              {step > 1 ? <button onClick={handleBack} className="flex items-center gap-2 text-slate-600 hover:text-brand-navy font-medium transition-colors" data-id="element-607" data-mp="2w8xt2c5wdi3h">
                  <ChevronLeft className="w-4 h-4" data-id="element-608" data-mp="17pjrbpayzhwv" /> Back
                </button> : <div data-id="element-609" data-mp="1bqn9iighpq19" />}

              <Button onClick={handleNext} disabled={!isStepValid()} variant="primary" className="gap-2" data-id="element-610" data-mp="1gnm1aucolkwa">
                {step === 4 ? 'Confirm Booking' : 'Continue'}{' '}
                <ChevronRight className="w-4 h-4" data-id="element-611" data-mp="nx54b52gpl3v" />
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}